import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, InputAdornment, MenuItem, Pagination, Skeleton, Switch, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdClose, MdDeleteOutline, MdEdit, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { deleteData, getData, postFileUpload, putData } from '../../Webservice/Webservice';
import CATEGORY_ROUTES from '../../API_Route/CategoryRoute';
import Subcategories from '../../components/Categories/Subcategories';
import { FONT, ImagePicker, Kpi, Row, StatusChip, Thumb, authToken, cell, fmtDate, head, iconBtn, person, yellowBtn } from '../../components/Categories/categoryShared';

const LIMIT = 15;
const emptyForm = { id: null, title: '', cluster: '', status: 'active', imageFile: null, imagePreview: '', originalTitle: '', subcategoryCount: 0 };

function CategoriesTab({ onNotice }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, noCluster: 0 });
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [cluster, setCluster] = useState('');
  const seq = useRef(0);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    getData(CATEGORY_ROUTES.LIST_CATEGORY_CLUSTER, { limit: 1000 }, authToken(), false).then((r) => setClusters(r?.clusters || [])).catch(() => setClusters([]));
  }, []);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, status, categoryCluster: cluster };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData(CATEGORY_ROUTES.GET_CATEGORY, params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(res?.categories || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load categories.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, status, cluster]);
  useEffect(() => { load(); }, [load]);

  const dirty = !!search || !!status || !!cluster;

  const openForm = (c = null) => {
    setViewing(null);
    setErrors({});
    setFormError('');
    setForm(c ? {
      id: c._id, title: c.title, cluster: c.categoryCluster?._id || c.categoryCluster || '', status: c.status || 'active',
      imageFile: null, imagePreview: c.image || '', originalTitle: c.title, subcategoryCount: c.subcategoryCount || 0,
    } : emptyForm);
    setFormOpen(true);
  };

  const save = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const e = {};
    if (!form.title.trim()) e.title = 'Category name is required';
    else if (form.title.trim().length > 80) e.title = 'Keep it under 80 characters';
    if (!form.cluster) e.cluster = 'Choose a cluster';
    if (!form.id && !form.imageFile) e.image = 'Upload an image';
    setErrors((x) => ({ ...e, image: e.image || x.image }));
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const body = new FormData();
      body.append('title', form.title.trim());
      body.append('status', form.status);
      body.append('categoryCluster', form.cluster);
      if (form.imageFile) body.append('image', form.imageFile);
      if (form.id) await putData(`categories/update/${form.id}`, body, authToken(), false);
      else await postFileUpload(CATEGORY_ROUTES.ADD_CATEGORY, body, authToken(), false);
      setFormOpen(false);
      onNotice(form.id ? 'Category updated.' : 'Category created.');
      load();
    } catch (err) {
      setFormError(err?.message || 'Could not save the category.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    try {
      await putData(`categories/update/${c._id}`, { status: c.status === 'active' ? 'inactive' : 'active' }, authToken(), false);
      load();
    } catch { /* the service shows the error */ }
  };

  const openView = async (c) => {
    setViewing(c);
    try {
      const res = await getData(`categories/${c._id}`, undefined, authToken(), false);
      if (res?._id) setViewing((cur) => (cur && cur._id === c._id ? { ...cur, ...res } : cur));
    } catch { /* keep the row data */ }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteData(`categories/delete/${toDelete._id}`, authToken());
      setToDelete(null);
      onNotice('Category deleted.');
      if (rows.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the category.');
    } finally {
      setDeleting(false);
    }
  };

  const renaming = form.id && form.title.trim() && form.title.trim().toLowerCase() !== form.originalTitle.toLowerCase();

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
        <Button startIcon={<MdAdd />} variant="contained" onClick={() => openForm()} sx={yellowBtn}>Add category</Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="All categories" value={stats.total} from="#6366f1" to="#4338ca" active={!status && !cluster} onClick={() => { setStatus(''); setCluster(''); setPage(1); }} />
        <Kpi title="Active" value={stats.active} from="#22c55e" to="#15803d" active={status === 'active'} onClick={() => { setStatus(status === 'active' ? '' : 'active'); setPage(1); }} />
        <Kpi title="Inactive" value={stats.inactive} from="#94a3b8" to="#475569" active={status === 'inactive'} onClick={() => { setStatus(status === 'inactive' ? '' : 'inactive'); setPage(1); }} />
        <Kpi title="Without a cluster" value={stats.noCluster} from="#f59e0b" to="#b45309" active={cluster === 'none'} onClick={() => { setCluster(cluster === 'none' ? '' : 'none'); setPage(1); }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search category name…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Cluster" value={cluster} onChange={(e) => { setCluster(e.target.value); setPage(1); }} sx={{ minWidth: 190 }}>
          <MenuItem value="">All clusters</MenuItem><MenuItem value="none">No cluster</MenuItem>
          {clusters.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        {dirty && <Button onClick={() => { setSearch(''); setStatus(''); setCluster(''); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Category</TableCell>
                <TableCell sx={head}>Cluster</TableCell>
                <TableCell sx={head} align="center">Subcategories</TableCell>
                <TableCell sx={head} align="center">Products</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>{dirty ? 'No categories match these filters.' : 'No categories yet. Add the first one.'}</TableCell></TableRow>}
              {!loading && rows.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell sx={cell}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Thumb item={c} />
                      <Box>
                        <Typography onClick={() => openView(c)} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, cursor: 'pointer', '&:hover': { color: '#4338ca' } }}>{c.title}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{c.categoryCode || ''}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{c.categoryCluster?.name ? <Chip size="small" label={c.categoryCluster.name} sx={{ fontFamily: FONT, fontWeight: 700, background: '#fef3c7', color: '#92400e' }} /> : <span style={{ color: '#dc2626', fontSize: 13 }}>None</span>}</TableCell>
                  <TableCell align="center"><Chip size="small" label={c.subcategoryCount || 0} sx={{ fontWeight: 800, height: 22, background: c.subcategoryCount ? '#e0e7ff' : '#f1f5f9', color: c.subcategoryCount ? '#3730a3' : '#94a3b8' }} /></TableCell>
                  <TableCell align="center"><Chip size="small" label={c.productCount || 0} sx={{ fontWeight: 800, height: 22, background: c.productCount ? '#dcfce7' : '#f1f5f9', color: c.productCount ? '#166534' : '#94a3b8' }} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Switch size="small" checked={c.status === 'active'} onChange={() => toggle(c)} />
                      <StatusChip active={c.status === 'active'} />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => openView(c)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openForm(c)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title={c.subcategoryCount || c.productCount ? 'In use' : 'Delete'}><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(c); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} categor{meta.total === 1 ? 'y' : 'ies'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* create / edit */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2.25, borderBottom: '3px solid rgb(255 209 2)', background: '#fff' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{form.id ? 'Edit category' : 'Add category'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{form.id ? form.originalTitle : 'Products are organised under categories'}</Typography>
          </Box>
          <IconButton onClick={() => setFormOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <Box component="form" onSubmit={save} noValidate sx={{ display: 'contents' }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ImagePicker file={form.imageFile} preview={form.imagePreview} name={form.title} error={errors.image} onPick={(f, err) => (err ? setErrors((x) => ({ ...x, image: err })) : (setErrors((x) => ({ ...x, image: undefined })), setForm((fm) => ({ ...fm, imageFile: f }))))} />
            <TextField autoFocus size="small" label="Category name *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} error={!!errors.title} helperText={errors.title} inputProps={{ maxLength: 80 }} />
            <TextField select size="small" label="Cluster *" value={form.cluster} onChange={(e) => setForm({ ...form, cluster: e.target.value })} error={!!errors.cluster} helperText={errors.cluster || 'The group this category belongs to'}>
              {clusters.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}{c.status !== 'active' ? ' (inactive)' : ''}</MenuItem>)}
            </TextField>
            <FormControlLabel control={<Switch checked={form.status === 'active'} onChange={(e) => setForm({ ...form, status: e.target.checked ? 'active' : 'inactive' })} />} label={form.status === 'active' ? 'Active — shown on the storefront' : 'Inactive — hidden'} />
            {renaming && <Alert severity="info">Renaming also changes the category’s web address (slug).{form.subcategoryCount ? ` Its ${form.subcategoryCount} subcategor${form.subcategoryCount === 1 ? 'y' : 'ies'} stay under it.` : ''}</Alert>}
            {formError && <Alert severity="error">{formError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setFormOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : form.id ? 'Save changes' : 'Add category'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* view */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {viewing && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Thumb item={viewing} size={52} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{viewing.title}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>{viewing.categoryCode || ''}{viewing.categoryCluster?.name ? ` · ${viewing.categoryCluster.name}` : ''}</Typography>
              </Box>
              <StatusChip active={viewing.status === 'active'} />
              <IconButton onClick={() => setViewing(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #f59e0b', borderRadius: '14px', p: 2.25 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 0.5 }}>Details</Typography>
                <Row label="Cluster" value={viewing.categoryCluster?.name} />
                <Row label="Web address" value={viewing.slug} />
                <Row label="Products" value={viewing.productCount ?? 0} />
                <Row label="Added" value={fmtDate(viewing.createdAt)} />
                <Row label="Added by" value={person(viewing.createdBy)} />
                <Row label="Last updated" value={fmtDate(viewing.updatedAt)} />
              </Box>
              <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #22c55e', borderRadius: '14px', p: 2.25 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>Subcategories</Typography>
                {!viewing.subcategories && <Skeleton height={44} />}
                {viewing.subcategories && viewing.subcategories.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>None yet. Add them in the Subcategories tab.</Typography>}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {(viewing.subcategories || []).map((s) => <Chip key={s._id} label={s.title} size="small" sx={{ fontFamily: FONT, fontWeight: 700, background: s.status === 'active' ? '#e0e7ff' : '#f1f5f9', color: s.status === 'active' ? '#3730a3' : '#94a3b8', textDecoration: s.status === 'active' ? 'none' : 'line-through' }} />)}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setViewing(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              <Button onClick={() => openForm(viewing)} startIcon={<MdEdit />} variant="contained" sx={yellowBtn}>Edit category</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* delete */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete category?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            <b>{toDelete?.title}</b> will be removed (a copy is archived).{toDelete?.subcategoryCount || toDelete?.productCount ? ` It still has ${toDelete.subcategoryCount || 0} subcategor${toDelete.subcategoryCount === 1 ? 'y' : 'ies'} and ${toDelete.productCount || 0} product(s), so the server will refuse. Switch it off to hide it instead.` : ' Nothing uses it.'}
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Categories() {
  const [tab, setTab] = useState(0);
  const [notice, setNotice] = useState('');

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Categories</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>How your products are grouped: clusters hold categories, categories hold subcategories.</Typography>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e2e8f0', '& .MuiTab-root': { textTransform: 'none', fontFamily: FONT, fontWeight: 700 } }}>
        <Tab label="Categories" />
        <Tab label="Subcategories" />
      </Tabs>
      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}
      {tab === 0 ? <CategoriesTab onNotice={setNotice} /> : <Subcategories onNotice={setNotice} />}
    </Box>
  );
}

export default Categories;
